import { useCallback, useState } from 'react';

import { Text } from '@/components/shared/Text/Text';
import { Funnel } from '@/lib/types';
import { getAmplitudeUserLink } from '@/lib/utils';
import { User, UserConversion } from '@addmile/backend-client';

import EditEmailModal from '../EditEmailModal/EditEmailModal';
import EditNameModal from '../EditNameModal/EditNameModal';
import AddChatMinutesModal from '../UserBilling/components/AddChatMinutesModal';
import { AuthMagicLink } from './AuthMagicLink/AuthMagicLink';
import { EmailCopy } from './EmailCopy/EmailCopy';
import ResetPassword from './ResetPassword/ResetPassword';
import UserDelete from './UserDelete/UserDelete';
import { UserProfileInfo } from './UserProfileInfo/UserProfileInfo';

interface UserProfileProps {
  user: User;
  conversion: UserConversion | null;
  balance: number;
}

const UserProfile = ({ user, conversion, balance }: UserProfileProps) => {
  const [showEditEmailModal, setShowEditEmailModal] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showAddChatMinutesModal, setShowAddChatMinutesModal] = useState(false);

  const openEditEmailModal = useCallback(() => setShowEditEmailModal(true), []);
  const openEditNameModal = useCallback(() => setShowEditNameModal(true), []);
  const openAddChatMinutesModal = useCallback(() => setShowAddChatMinutesModal(true), []);

  const userInfoData = [
    { title: 'Id', value: user.id },
    {
      title: 'Email',
      value: <EmailCopy onClick={openEditEmailModal} userEmail={user.email} />,
    },
    {
      title: 'Name',
      value: (
        <div className="col-span-3 flex items-center">
          <Text.Normal>{user.name ?? ''}</Text.Normal>
          <div className="ml-auto flex items-center">
            <img
              onClick={openEditNameModal}
              src="/icons/pencil.svg"
              alt="edit"
              className="ml-2 cursor-pointer"
            />
          </div>
        </div>
      ),
    },
    { title: 'Funnel', value: (conversion?.funnel as Funnel)?.name },
    { title: 'Platform', value: conversion?.platform },
    {
      title: 'Amplitude',
      value: (
        <a
          className="!text-primary-50"
          href={getAmplitudeUserLink(user.id)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {user.id}
        </a>
      ),
    },
    {
      title: 'Minutes balance',
      value: (
        <div className="col-span-3 flex items-center">
          <Text.Normal>{balance}</Text.Normal>
          <div className="ml-auto flex items-center">
            <img
              onClick={openAddChatMinutesModal}
              src="/icons/pencil.svg"
              alt="edit"
              className="ml-2 cursor-pointer"
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white-100 p-6 rounded-[16px] w-[624px] flex flex-col align-center divide-y divide-neutral-10 animation-appear">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <Text.H5 weight="bold" className="color-neutral-80">
          {user.name ?? 'User Data'}
          {user.deletedAt && (
            <span className="text-danger-50 font-semibold text-base ml-2">{'(deleted)'}</span>
          )}
        </Text.H5>
        <div className="flex flex-wrap gap-3 items-center">
          <AuthMagicLink userId={user.id} />
          <ResetPassword userId={user.id} />
          <UserDelete userId={user.id} deleted={!!user.deletedAt} />
        </div>
      </div>
      {userInfoData.map((data) => (
        <UserProfileInfo key={data.title} title={data.title} value={data?.value ?? ''} />
      ))}

      <EditEmailModal
        isPopupOpen={showEditEmailModal}
        closePopup={() => setShowEditEmailModal(false)}
        userEmail={user.email}
      />

      <EditNameModal
        isPopupOpen={showEditNameModal}
        closePopup={() => setShowEditNameModal(false)}
        userName={user.name || ''}
      />

      <AddChatMinutesModal
        isPopupOpen={showAddChatMinutesModal}
        closePopup={() => setShowAddChatMinutesModal(false)}
      />
    </div>
  );
};

export default UserProfile;
